import { $color, $size } from "@/utils/cssVarUtils"
import { switchAnatomy as parts } from "@chakra-ui/anatomy"
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
  SystemStyleObject,
} from "@chakra-ui/theme-tools"
import { calc, cssVar } from "@chakra-ui/theme-tools"

const $width = cssVar("switch-track-width")
const $height = cssVar("switch-track-height")

const $diff = cssVar("switch-track-diff")
const diffValue = calc.subtract($width, $height)

const $translateX = cssVar("switch-thumb-x")

const baseStyleTrack: SystemStyleFunction = (props) => {
  return {
    borderRadius: "full",
    p: "2px",
    width: [$width.reference],
    height: [$height.reference],
    transitionProperty: "common",
    transitionDuration: "fast",
    bg: $color('bg-input-normal'),
    _focusVisible: {
      boxShadow: "outline",
    },
    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
    },
    _checked: {
      bg: $color('scene-primary-normal'),
      _hover: {
        bg: $color('scene-primary-active'),
      },
    },
    _invalid: {
      boxShadow: `inset 0 0 0 1px ${$color('scene-danger-normal')}`
    },
  }
}

const baseStyleThumb: SystemStyleObject = {
  bg: "white",
  transitionProperty: "transform",
  transitionDuration: "normal",
  borderRadius: "inherit",
  width: [$height.reference],
  height: [$height.reference],
  _checked: {
    transform: `translateX(${$translateX.reference})`,
  },
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  container: {
    [$diff.variable]: diffValue,
    [$translateX.variable]: $diff.reference,
    _rtl: {
      [$translateX.variable]: calc($diff).negate().toString(),
    },
  },
  track: baseStyleTrack(props),
  thumb: baseStyleThumb,
})

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  xs: {
    container: {
      [$width.variable]: $size('3xl'),
      [$height.variable]: $size('md'),
    },
  },
  sm: {
    container: {
      [$width.variable]: $size('5xl'),
      [$height.variable]: $size('2xl'),
    },
  },
  md: {
    container: {
      [$width.variable]: $size('7xl'),
      [$height.variable]: $size('4xl'),
    },
  },
  lg: {
    container: {
      [$width.variable]: $size('9xl'),
      [$height.variable]: $size('5xl'),
    },
  },
}

const defaultProps = {
  size: "xs",
  colorScheme: "blue",
}

export const Switch = {
  parts: parts.keys,
  baseStyle,
  sizes,
  defaultProps,
}